import { MailIcon, Trash2Icon } from "lucide-react";
import type { FormEvent, ReactNode } from "react";

import { useI18n } from "~/i18n";
import type { OrgClerkRole } from "../../orgMembers";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { Spinner } from "../ui/spinner";
import { MorrowPersonAvatar, personRoleLine } from "../workspace/MorrowPersonCard";

export type OrganizationMemberRow = {
  readonly id: string;
  readonly userId: string;
  readonly name: string;
  readonly email: string;
  readonly role: OrgClerkRole;
  readonly imageUrl?: string;
  readonly avatarSeed?: string;
};

export type OrganizationInvitationRow = {
  readonly id: string;
  readonly emailAddress: string;
  readonly role: OrgClerkRole;
};

function roleLabel(role: string, t: ReturnType<typeof useI18n>["t"]): string {
  return role === "org:admin"
    ? t("settings.organization.roleAdmin")
    : t("settings.organization.roleMember");
}

export function OrganizationMembersPanelView({
  isAdmin,
  members,
  invitations,
  membersLoading,
  email,
  role,
  inviteError,
  invitePending,
  actionKey,
  onEmailChange,
  onRoleChange,
  onInvite,
  onMemberRoleChange,
  onRemoveMember,
  onRevokeInvitation,
}: {
  readonly isAdmin: boolean;
  readonly members: readonly OrganizationMemberRow[];
  readonly invitations: readonly OrganizationInvitationRow[];
  readonly membersLoading: boolean;
  readonly email: string;
  readonly role: OrgClerkRole;
  readonly inviteError: string | null;
  readonly invitePending: boolean;
  readonly actionKey: string | null;
  readonly onEmailChange: (value: string) => void;
  readonly onRoleChange: (role: OrgClerkRole) => void;
  readonly onInvite: (event: FormEvent) => void;
  readonly onMemberRoleChange: (userId: string, role: OrgClerkRole) => void;
  readonly onRemoveMember: (userId: string) => void;
  readonly onRevokeInvitation: (invitationId: string) => void;
}) {
  const { t } = useI18n();

  return (
    <div className="space-y-4 px-3 sm:px-4">
      {isAdmin ? (
        <form
          onSubmit={onInvite}
          className="space-y-3 rounded-xl border border-border/60 bg-background/60 p-4"
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="min-w-0 flex-1 space-y-1.5">
              <label
                htmlFor="organization-invite-email"
                className="text-sm font-medium text-foreground"
              >
                {t("settings.organization.inviteTitle")}
              </label>
              <Input
                id="organization-invite-email"
                type="email"
                value={email}
                onChange={(event) => onEmailChange(event.currentTarget.value)}
                placeholder={t("settings.organization.inviteEmailPlaceholder")}
                autoComplete="email"
              />
            </div>
            <div className="flex w-full flex-col gap-1.5 sm:w-40">
              <span className="text-sm font-medium text-foreground">
                {t("onboarding.invitePerson.access")}
              </span>
              <Select
                value={role}
                onValueChange={(value) => {
                  if (value === "org:admin" || value === "org:member") onRoleChange(value);
                }}
              >
                <SelectTrigger aria-label={t("onboarding.invitePerson.access")}>
                  <SelectValue>{roleLabel(role, t)}</SelectValue>
                </SelectTrigger>
                <SelectPopup align="end">
                  <SelectItem value="org:member">
                    {t("settings.organization.roleMember")}
                  </SelectItem>
                  <SelectItem value="org:admin">{t("settings.organization.roleAdmin")}</SelectItem>
                </SelectPopup>
              </Select>
            </div>
            <Button type="submit" size="sm" disabled={invitePending} className="shrink-0">
              {invitePending ? <Spinner /> : <MailIcon />}
              {invitePending
                ? t("settings.organization.inviting")
                : t("settings.organization.inviteAction")}
            </Button>
          </div>
          {inviteError ? (
            <p className="text-xs text-destructive">{inviteError}</p>
          ) : (
            <p className="text-[13px] text-muted-foreground/80">
              {t("settings.organization.inviteDescription")}
            </p>
          )}
        </form>
      ) : null}

      <div className="overflow-hidden rounded-xl border border-border/60">
        {membersLoading ? (
          <div className="flex items-center justify-center gap-2 px-4 py-10 text-sm text-muted-foreground">
            <Spinner />
            {t("common.loading")}
          </div>
        ) : members.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-muted-foreground">
            {t("settings.organization.noMembers")}
          </p>
        ) : (
          <ul className="divide-y divide-border/60">
            {members.map((member) => {
              const pendingRole = actionKey === `role:${member.userId}`;
              const pendingRemove = actionKey === `remove:${member.userId}`;

              return (
                <li
                  key={member.id}
                  className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <MorrowPersonAvatar
                      name={member.name}
                      {...(member.imageUrl ? { imageUrl: member.imageUrl } : {})}
                      {...(member.avatarSeed ? { avatarSeed: member.avatarSeed } : {})}
                      size="md"
                    />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-foreground">
                        {member.name}
                      </div>
                      <div className="truncate text-[13px] text-muted-foreground/80">
                        {personRoleLine(member.email, roleLabel(member.role, t))}
                      </div>
                    </div>
                  </div>

                  {isAdmin ? (
                    <div className="flex items-center gap-2 sm:justify-end">
                      <Select
                        value={member.role}
                        onValueChange={(value) => {
                          if (value === "org:admin" || value === "org:member") {
                            onMemberRoleChange(member.userId, value);
                          }
                        }}
                      >
                        <SelectTrigger
                          className="w-full sm:w-36"
                          disabled={pendingRole || pendingRemove}
                          aria-label={t("settings.organization.changeRole", { name: member.name })}
                        >
                          <SelectValue>{roleLabel(member.role, t)}</SelectValue>
                        </SelectTrigger>
                        <SelectPopup align="end">
                          <SelectItem value="org:member">
                            {t("settings.organization.roleMember")}
                          </SelectItem>
                          <SelectItem value="org:admin">
                            {t("settings.organization.roleAdmin")}
                          </SelectItem>
                        </SelectPopup>
                      </Select>
                      <Button
                        type="button"
                        size="icon-sm"
                        variant="ghost-muted"
                        disabled={pendingRole || pendingRemove}
                        aria-label={t("settings.organization.removeMember", { name: member.name })}
                        onClick={() => onRemoveMember(member.userId)}
                      >
                        {pendingRemove ? <Spinner /> : <Trash2Icon className="size-4" />}
                      </Button>
                    </div>
                  ) : (
                    <span className="inline-flex w-fit items-center rounded-md border border-border/60 bg-muted/40 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                      {roleLabel(member.role, t)}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {invitations.length > 0 ? (
        <div className="space-y-2">
          <h4 className="px-1 text-sm font-medium text-foreground">
            {t("settings.organization.pendingInvitations")}
          </h4>
          <ul className="divide-y divide-border/60 overflow-hidden rounded-xl border border-border/60">
            {invitations.map((invitation) => {
              const pendingRevoke = actionKey === `revoke:${invitation.id}`;
              return (
                <li
                  key={invitation.id}
                  className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">
                      {invitation.emailAddress}
                    </div>
                    <div className="text-[13px] text-muted-foreground/80">
                      {personRoleLine(t("team.invited"), roleLabel(invitation.role, t))}
                    </div>
                  </div>
                  {isAdmin ? (
                    <Button
                      type="button"
                      size="xs"
                      variant="outline"
                      disabled={pendingRevoke}
                      onClick={() => onRevokeInvitation(invitation.id)}
                    >
                      {pendingRevoke ? <Spinner /> : null}
                      {t("settings.organization.revokeInvitation")}
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function OrganizationSettingsDisabledHint({ children }: { readonly children: ReactNode }) {
  return (
    <section className="card-glass space-y-4 rounded-lg border border-transparent p-6 text-sm text-muted-foreground">
      <p>{children}</p>
    </section>
  );
}
